<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <a href="/services" class="breadcrumb-link">Business Services</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">Print Fire License</span>
            </nav>
            <h1 class="page-title">Print Fire License</h1>
            <p class="page-subtitle">Enter your Business ID or Invoice Number to view and print your fire safety certificate</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Card -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
                    </svg>
                  </div>
                  <h3>Fire Safety Certificate</h3>
                </div>
                <p class="info-description">
                  Fire Safety Compliance Certificates are mandatory for all commercial establishments to ensure fire safety standards are met and protect lives and property.
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        <path d="M9 12l2 2 4-4"></path>
                      </svg>
                    </div>
                    <div>
                      <strong>Safety Compliance</strong>
                      <p>Ensure your premises meet standards</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Valid for 1 Year</strong>
                      <p>Annual renewal required</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>Digital Certificate</strong>
                      <p>Download after approval</p>
                    </div>
                  </div>
                </div>

                <div class="info-help">
                  <h4>Who Needs This Certificate?</h4>
                  <ul>
                    <li>Commercial Buildings & Offices</li>
                    <li>Hotels & Restaurants</li>
                    <li>Shopping Centers & Markets</li>
                    <li>Factories & Warehouses</li>
                    <li>Schools & Hospitals</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Right Side - Form/Details -->
            <div class="form-panel">
              <!-- Search Form -->
              <div v-if="searchLicense" class="form-card">
                <div class="form-card-header">
                  <div class="form-step-indicator">
                    <span class="step-number">1</span>
                    <span class="step-text">Enter Business ID or Invoice Number</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <p class="form-intro">
                    Enter your Business ID or Invoice Number to retrieve your fire safety certificate details.
                  </p>

                  <!-- Search Method Toggle -->
                  <div class="search-method-toggle">
                    <button
                        class="toggle-btn"
                        :class="{ active: searchMethod === 'businessID' }"
                        @click="searchMethod = 'businessID'"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                      Business ID
                    </button>
                    <button
                        class="toggle-btn"
                        :class="{ active: searchMethod === 'invoiceNo' }"
                        @click="searchMethod = 'invoiceNo'"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                      </svg>
                      Invoice Number
                    </button>
                  </div>

                  <div class="search-form">
                    <!-- Business ID Input -->
                    <div v-if="searchMethod === 'businessID'" class="form-group">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        Business ID
                      </label>
                      <div class="input-with-icon">
                        <input
                            v-model="businessID"
                            type="text"
                            class="form-input"
                            placeholder="e.g., BUS-2024-00123"
                            @keyup.enter="searchFireLicense"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                          </svg>
                        </span>
                      </div>
                      <span class="form-hint">Enter the Business ID exactly as it appears on your documents</span>
                    </div>

                    <!-- Invoice Number Input -->
                    <div v-if="searchMethod === 'invoiceNo'" class="form-group">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                        Invoice Number
                      </label>
                      <div class="input-with-icon">
                        <input
                            v-model="invoiceNo"
                            type="text"
                            class="form-input"
                            placeholder="e.g., INV-2024-00456"
                            @keyup.enter="searchFireLicense"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                        </span>
                      </div>
                      <span class="form-hint">Enter the Invoice Number from your fire license bill</span>
                    </div>

                    <!-- Error Message -->
                    <div v-if="message" class="alert alert-error">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      <span>{{ message }}</span>
                      <button class="alert-close" @click="message = null">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>

                    <button
                        @click="searchFireLicense"
                        class="btn btn-primary btn-block"
                        :disabled="loading"
                    >
                      <div v-if="loading" class="spinner white"></div>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                      <span>{{ loading ? 'Searching...' : 'Find Certificate' }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- License Details -->
              <div v-if="licenseDetails" class="form-card license-card">
                <div class="form-card-header">
                  <button class="back-btn" @click="goBack">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                  </button>
                  <div class="license-status" :class="getStatusClass(license.status)">
                    <span class="status-dot"></span>
                    {{ license.status || 'Active' }}
                  </div>
                </div>

                <div class="form-card-body">
                  <!-- Business Header -->
                  <div class="business-header">
                    <div class="business-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"></path>
                      </svg>
                    </div>
                    <div class="business-info">
                      <h2 class="business-name">{{ license.businessName }}</h2>
                      <span class="business-id">{{ license.businessID }}</span>
                    </div>
                  </div>

                  <!-- License Details Grid -->
                  <div class="details-grid">
                    <div class="detail-item">
                      <span class="detail-label">Certificate Type</span>
                      <span class="detail-value">{{ license.certificateType || 'Fire Safety Certificate' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Permit Category</span>
                      <span class="detail-value">{{ license.permitCategory || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Location</span>
                      <span class="detail-value">{{ license.subCountyName }}, {{ license.wardName }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Physical Address</span>
                      <span class="detail-value">{{ license.physicalAddress || license.buildingName || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Owner</span>
                      <span class="detail-value">{{ license.fullNames || 'N/A' }}</span>
                    </div>

                    <div class="detail-item">
                      <span class="detail-label">Phone</span>
                      <span class="detail-value">{{ license.ownerPhone || 'N/A' }}</span>
                    </div>
                  </div>

                  <!-- Fire Safety Measures -->
                  <div v-if="license.fireMeasures && license.fireMeasures.length > 0" class="safety-measures">
                    <h4 class="measures-title">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        <path d="M9 12l2 2 4-4"></path>
                      </svg>
                      Fire Safety Measures in Place
                    </h4>
                    <div class="measures-list">
                      <div v-for="(measure, index) in license.fireMeasures" :key="index" class="measure-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        {{ measure }}
                      </div>
                    </div>
                  </div>

                  <!-- Validity Card -->
                  <div class="validity-card" :class="{ expired: isExpired }">
                    <div class="validity-header">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span>Certificate Validity</span>
                    </div>
                    <div class="validity-dates">
                      <div class="validity-date">
                        <span class="date-label">Issue Date</span>
                        <span class="date-value">{{ formatDate(license.startDate) }}</span>
                      </div>
                      <div class="validity-arrow">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </div>
                      <div class="validity-date">
                        <span class="date-label">Expiry Date</span>
                        <span class="date-value" :class="{ 'text-expired': isExpired }">{{ formatDate(license.endDate) }}</span>
                      </div>
                    </div>
                    <div v-if="isExpired" class="expiry-warning">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                      </svg>
                      <span>This certificate has expired. Please renew to maintain compliance.</span>
                    </div>
                  </div>

                  <!-- Print Button -->
                  <div class="action-buttons">
                    <button @click="printLicense" class="btn btn-primary btn-lg">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="6 9 6 2 18 2 18 9"></polyline>
                        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                        <rect x="6" y="14" width="12" height="8"></rect>
                      </svg>
                      <span>Print Certificate</span>
                    </button>

                    <button v-if="isExpired" @click="renewLicense" class="btn btn-success btn-lg">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                      </svg>
                      <span>Renew Certificate</span>
                    </button>
                  </div>

                  <!-- Additional Info -->
                  <div class="additional-info">
                    <div class="info-row">
                      <span class="info-label">Invoice Number</span>
                      <span class="info-value">{{ license.billNo || 'N/A' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Amount Paid</span>
                      <span class="info-value">KES {{ formatAmount(license.receiptAmount) }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Last Inspection</span>
                      <span class="info-value">{{ license.lastInspection || 'N/A' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">Issued By</span>
                      <span class="info-value">{{ license.issuingOfficer || 'County Fire Department' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <FooterComponent />
  </div>
</template>

<script>
import { execute, fire } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "PrintFireLicense",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      businessID: '',
      invoiceNo: '',
      searchMethod: 'businessID',
      message: null,
      loading: false,
      searchLicense: true,
      licenseDetails: false,
      license: {
        id: null,
        billNo: null,
        businessID: null,
        businessName: null,
        duration: null,
        billTotal: null,
        receiptAmount: null,
        billBalance: null,
        startDate: null,
        endDate: null,
        statusID: null,
        status: null,
        printable: null,
        issuingOfficer: null,
        designation: null,
        dateOfIssue: null,
        stageStatus: null,
        comments: null,
        incomeTypeId: null,
        feeID: null,
        subCountyName: null,
        wardName: null,
        dateCreated: null,
        subCountyID: null,
        wardID: null,
        plotNumber: null,
        physicalAddress: null,
        buildingName: null,
        buildingOccupancy: null,
        floorNo: null,
        roomNo: null,
        premiseSize: null,
        numberOfEmployees: null,
        businessCategory: null,
        businessSubCategory: null,
        incomeTypeID: null,
        businessEmail: null,
        postalAddress: null,
        postalCode: null,
        businessPhone: null,
        contactPersonNames: null,
        contactPersonIDNo: null,
        businessRole: null,
        contactPersonPhone: null,
        contactPersonEmail: null,
        fullNames: null,
        ownerID: null,
        ownerPhone: null,
        ownerEmail: null,
        kraPin: null,
        createdBy: null,
        createdByIDNo: null,
        lat: null,
        lng: null,
        lastInspection: null,
        certificateType: null,
        permitCategory: null,
        fireMeasures: []
      }
    };
  },

  computed: {
    isExpired() {
      if (!this.license.endDate) return false;
      return new Date(this.license.endDate) < new Date();
    }
  },

  mounted() {
    // Check URL param or query string for businessID or invoiceNo
    const businessID = this.$route.params.businessID || this.$route.query.businessID;
    const invoiceNo = this.$route.params.invoiceNo || this.$route.query.invoiceNo;

    if (businessID) {
      this.businessID = businessID;
      this.searchMethod = 'businessID';
      this.searchFireLicense();
    } else if (invoiceNo) {
      this.invoiceNo = invoiceNo;
      this.searchMethod = 'invoiceNo';
      this.searchFireLicense();
    }
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      } catch {
        return dateStr;
      }
    },

    formatAmount(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    getStatusClass(status) {
      if (!status) return 'active';
      const s = status.toLowerCase();
      if (s.includes('active') || s.includes('approved') || s.includes('paid')) return 'active';
      if (s.includes('expired')) return 'expired';
      if (s.includes('pending')) return 'pending';
      return 'active';
    },

    goBack() {
      this.searchLicense = true;
      this.licenseDetails = false;
      this.message = null;
    },

    printLicense() {
      localStorage['params'] = JSON.stringify({
        businessID: this.license.businessID,
        billNo: this.license.billNo
      });
      const routeData = this.$router.resolve({ name: 'fire-certificate' });
      window.open(routeData.href, '_blank');
    },

    renewLicense() {
      this.$router.push({ name: 'fire-license', query: { businessID: this.license.businessID } });
    },

    searchFireLicense() {
      // Validate input
      if (this.searchMethod === 'businessID' && this.businessID === '') {
        this.message = "Please enter a Business ID";
        return;
      }
      if (this.searchMethod === 'invoiceNo' && this.invoiceNo === '') {
        this.message = "Please enter an Invoice Number";
        return;
      }

      this.loading = true;
      this.message = null;

      const data = new FormData();
      data.append("function", "getFireLicense");

      if (this.searchMethod === 'businessID') {
        data.append("businessID", this.businessID);
      } else {
        data.append("billNo", this.invoiceNo);
      }

      execute(data, fire)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.searchLicense = false;
              this.licenseDetails = true;
              this.license = res.data.data.license;

              // Parse fire measures if it's a string
              if (this.license.fireMeasures && typeof this.license.fireMeasures === 'string') {
                try {
                  this.license.fireMeasures = JSON.parse(this.license.fireMeasures);
                } catch {
                  this.license.fireMeasures = this.license.fireMeasures.split(',').map(m => m.trim());
                }
              }
            } else {
              this.message = res.data.message || "No fire license found for the provided details";
            }
          })
          .catch((e) => {
            this.loading = false;
            this.message = e.message || "An error occurred while searching";
          });
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Content Section */
.content-section {
  padding: 48px 0 80px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 32px;
  align-items: start;
}

/* Info Panel */
.info-panel {
  position: sticky;
  top: 96px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
}

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.info-description {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
}

.info-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
}

.feature-icon {
  width: 40px;
  height: 40px;
  background: #d1fae5;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #059669;
  flex-shrink: 0;
}

.feature-item strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 2px;
}

.feature-item p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.info-help {
  padding: 20px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
}

.info-help h4 {
  font-size: 14px;
  font-weight: 600;
  color: #991b1b;
  margin-bottom: 12px;
}

.info-help ul {
  margin: 0;
  padding-left: 20px;
}

.info-help li {
  font-size: 13px;
  color: #b91c1c;
  margin-bottom: 6px;
}

/* Form Panel */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-card-header {
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.form-step-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-number {
  width: 32px;
  height: 32px;
  background: #dc2626;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.step-text {
  font-weight: 600;
  color: #1e293b;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.license-status {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
}

.license-status.active {
  background: #d1fae5;
  color: #059669;
}

.license-status.expired {
  background: #fee2e2;
  color: #dc2626;
}

.license-status.pending {
  background: #fef3c7;
  color: #d97706;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
}

.form-card-body {
  padding: 32px;
}

.form-intro {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Search Method Toggle */
.search-method-toggle {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.toggle-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn:hover {
  border-color: #cbd5e1;
  color: #475569;
}

.toggle-btn.active {
  background: #fef2f2;
  border-color: #dc2626;
  color: #dc2626;
}

/* Form Elements */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.input-with-icon {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  padding-right: 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #dc2626;
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1);
}

.form-input::placeholder {
  color: #94a3b8;
}

.input-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-hint {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: inherit;
  opacity: 0.7;
}

.alert-close:hover { opacity: 1; }

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block {
  width: 100%;
}

.btn-lg {
  padding: 16px 32px;
  font-size: 16px;
}

.btn-primary {
  background: #dc2626;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #b91c1c;
  transform: translateY(-1px);
}

.btn-success {
  background: #059669;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #047857;
  transform: translateY(-1px);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.white {
  border-color: rgba(255,255,255,0.3);
  border-top-color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Business Header */
.business-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border-radius: 14px;
  margin-bottom: 24px;
}

.business-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.business-name {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
}

.business-id {
  font-size: 14px;
  color: #dc2626;
  font-weight: 600;
  font-family: monospace;
  background: rgba(220, 38, 38, 0.1);
  padding: 4px 10px;
  border-radius: 6px;
}

/* Details Grid */
.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.detail-item {
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

/* Safety Measures */
.safety-measures {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 24px;
}

.measures-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #16a34a;
  margin-bottom: 16px;
}

.measures-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
}

.measure-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #166534;
  padding: 8px 12px;
  background: white;
  border-radius: 8px;
}

.measure-item svg {
  color: #22c55e;
  flex-shrink: 0;
}

/* Validity Card */
.validity-card {
  background: #f0fdf4;
  border: 2px solid #86efac;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 24px;
}

.validity-card.expired {
  background: #fef2f2;
  border-color: #fca5a5;
}

.validity-header {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #16a34a;
  font-weight: 600;
  margin-bottom: 16px;
}

.validity-card.expired .validity-header {
  color: #dc2626;
}

.validity-dates {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 16px;
  border-radius: 10px;
}

.validity-date {
  text-align: center;
}

.date-label {
  display: block;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.date-value {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

.date-value.text-expired {
  color: #dc2626;
}

.validity-arrow {
  color: #16a34a;
}

.validity-card.expired .validity-arrow {
  color: #dc2626;
}

.expiry-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px;
  background: white;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #dc2626;
  font-size: 13px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.action-buttons .btn {
  flex: 1;
}

/* Additional Info */
.additional-info {
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 13px;
  color: #64748b;
}

.info-value {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-panel {
    position: static;
  }
}

@media (max-width: 768px) {
  .form-card-body {
    padding: 24px;
  }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .measures-list {
    grid-template-columns: 1fr;
  }

  .validity-dates {
    flex-direction: column;
    gap: 16px;
  }

  .validity-arrow {
    transform: rotate(90deg);
  }

  .action-buttons {
    flex-direction: column;
  }

  .business-header {
    flex-direction: column;
    text-align: center;
  }

  .page-title {
    font-size: 26px;
  }

  .search-method-toggle {
    flex-direction: column;
  }
}
</style>